import { useCallback, useRef, useState } from 'react'
import { AllowedColSpan } from 'src/types/dashboard'
import { ALLOWED_COL_SPANS } from 'src/constants/dashboard'

interface UseColumnResizeParams {
  width: AllowedColSpan
  onResizeEnd: (width: AllowedColSpan) => void
  onResizePreview: (width: AllowedColSpan | null) => void
  containerRef: React.RefObject<HTMLElement | null>
}

function snapToAllowed(raw: number): AllowedColSpan {
  return ALLOWED_COL_SPANS.reduce((prev, curr) =>
    Math.abs(curr - raw) < Math.abs(prev - raw) ? curr : prev
  )
}

export function useColumnResize({
  width,
  onResizeEnd,
  onResizePreview,
  containerRef
}: UseColumnResizeParams) {
  const [isResizing, setIsResizing] = useState(false)
  const [previewWidth, setPreviewWidth] = useState<AllowedColSpan>(width)

  const startXRef = useRef(0)
  const initialWidthRef = useRef<AllowedColSpan>(width)
  const previewRef = useRef<AllowedColSpan>(width)
  const isResizingRef = useRef(false)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      startXRef.current = e.clientX
      initialWidthRef.current = width
      previewRef.current = width
      isResizingRef.current = true
      setPreviewWidth(width)
      setIsResizing(true)
      onResizePreview(width)
    },
    [width, onResizePreview]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isResizingRef.current || !containerRef.current) return
      const containerWidth = containerRef.current.getBoundingClientRect().width
      const colWidth = containerWidth / 12
      const deltaX = e.clientX - startXRef.current
      const deltaCols = deltaX / colWidth
      const rawWidth = initialWidthRef.current + deltaCols
      const clamped = Math.max(2, Math.min(12, rawWidth))
      const snapped = snapToAllowed(clamped)

      if (snapped !== previewRef.current) {
        previewRef.current = snapped
        setPreviewWidth(snapped)
        onResizePreview(snapped)
      }
    },
    [containerRef, onResizePreview]
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isResizingRef.current) return
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      isResizingRef.current = false
      setIsResizing(false)
      onResizeEnd(previewRef.current)
      onResizePreview(null)
    },
    [onResizeEnd, onResizePreview]
  )

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent) => {
      if (!isResizingRef.current) return
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      isResizingRef.current = false
      setPreviewWidth(initialWidthRef.current)
      setIsResizing(false)
      onResizePreview(null)
    },
    [onResizePreview]
  )

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    isResizing,
    previewWidth
  }
}
